import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import * as React from "react";
import { collectionListDataFrom, entryCard } from "@diality/contracts";
import { NewsList, NEWS_PAGINATION, } from "../components/blocks/news-list";
import { EntryCardGrid } from "../components/blocks/entry-card-grid";
import { FeaturedPress } from "./featured-press";
import { cn } from "../lib/utils";
import { BlockLink, useBlockRender } from "../render/context";
import { Button } from "../components/ui/button";
import { useBlockData, requestKey, } from "../render/data";
/**
 * CollectionList — a collection's published entries, in one of three shapes.
 *
 * The block an Editor places to put news on a page. It renders content it does
 * not own: the entries arrive through {@link useBlockData}, filled in by the
 * route loader on the public site and by react-query in the CMS, so a visitor
 * and a crawler both get the list in the first response.
 *
 * The three layouts reuse what already exists rather than inventing a fourth
 * card design — `news-grid` is the site's own news grid, `featured` is the
 * Featured Press panel. Only `cards` is new, and only because nothing in the
 * product drew an entry card with a picture.
 */
/** Turn one entry into the item the news grid takes, through the display map. */
function toNewsItem(entry, collection, resolveImage) {
    const card = entryCard({ display: collection?.display ?? {} }, entry, (ref) => resolveImage(ref));
    return {
        key: card.id,
        title: card.title,
        href: card.href,
        date: card.date,
        category: card.category,
        source: card.source,
        summary: card.summary,
        // An entry whose card leaves the site says so with the icon, not only in
        // the href — the arrow and the outbound glyph are how this site has always
        // told those apart.
        linkIcon: card.external ? "external" : "arrow",
        readMoreHref: card.href,
    };
}
export function CollectionListBlock({ data }) {
    const { placeholders, resolveImage } = useBlockRender();
    const request = requestFor(data);
    const { entries, collection, status, loadEntries, nextOffset } = useBlockData(request);
    const cards = entries.map((entry) => entryCard({ display: collection?.display ?? {} }, entry, (ref) => resolveImage(ref)));
    const items = React.useMemo(() => entries.map((entry) => toNewsItem(entry, collection, resolveImage)), [entries, collection, resolveImage]);
    const viewAllHref = collection?.basePath ?? "";
    /**
     * What the block says when it has nothing to show.
     *
     * Three different states, three different sentences. "Nothing published yet"
     * is a fact about the site; "we could not load these" is a fact about this
     * request, and saying the first when the second is true is a small lie the
     * page tells on every render. In the EDITOR a third reading applies: an
     * Editor who has not chosen a collection yet has not made a mistake.
     */
    const emptyMessage = (() => {
        if (status === "loading")
            return "Loading…";
        if (status === "error")
            return "These couldn’t be loaded just now.";
        if (!data.collectionId) {
            return placeholders === "editor" ? "Choose a collection to list." : "";
        }
        return "Nothing published here yet.";
    })();
    if (data.layout === "featured") {
        const [first, ...rest] = items;
        // The Featured Press panel needs a feature; with nothing published there is
        // nothing to feature, so the panel steps aside rather than rendering its
        // chrome around an empty hole.
        if (!first) {
            return (_jsx("section", { "data-collection-list": true, "data-collection-id": data.collectionId, "data-variant": "featured", className: "@container w-full px-[6.667cqw]", children: _jsx("p", { className: "text-muted-foreground", children: emptyMessage }) }));
        }
        const [featuredCard] = cards;
        return (_jsx("div", { "data-collection-list": true, "data-collection-id": data.collectionId, "data-variant": "featured", children: _jsx(FeaturedPress, { body: "", ctaLabel: data.viewAllLabel, ctaHref: viewAllHref, feature: {
                    id: first.key ?? first.title,
                    title: first.title,
                    href: first.href ?? viewAllHref,
                    date: first.date,
                    // The panel draws a picture when the entry has one and the
                    // collection maps a picture field. Neither is guaranteed — an
                    // imported release often has no image — so the panel needs a
                    // no-image branch, which is what it gained for this.
                    image: featuredCard?.image?.url,
                    imageAlt: featuredCard?.image?.alt,
                }, items: rest.map((item) => ({
                    id: item.key ?? item.title,
                    title: item.title,
                    href: item.href ?? viewAllHref,
                    date: item.date,
                })) }) }));
    }
    return (_jsxs("section", { "data-collection-list": true, "data-collection-id": data.collectionId, "data-variant": data.layout, className: cn("@container w-full px-[6.667cqw] @min-[701px]:px-[clamp(32px,7cqw,76px)] @min-[901px]:px-[clamp(40px,8cqw,88px)]"), children: [data.viewAllLabel && viewAllHref ? (_jsx("div", { className: "mx-auto mb-10 flex max-w-[1132px] justify-end @min-[701px]:mb-14", children: _jsx(Button, { asChild: true, variant: "outline", children: _jsx(BlockLink, { href: viewAllHref, children: data.viewAllLabel }) }) })) : null, _jsx("div", { className: "mx-auto w-full max-w-[1132px]", children: data.layout === "cards" ? (_jsx(EntryCardGrid, { entries: entries, display: collection?.display ?? {}, emptyMessage: emptyMessage })) : (_jsx(NewsList, { items: items, emptyMessage: emptyMessage, pagination: true, nextOffset: data.limit > 0 && (nextOffset ?? 0) >= data.limit
                        ? null
                        : nextOffset, loadMore: loadEntries && request
                        ? async (offset, signal) => {
                            const remaining = data.limit > 0
                                ? data.limit - offset
                                : NEWS_PAGINATION.batchSize;
                            if (remaining <= 0)
                                return { items: [], nextOffset: null };
                            const page = await loadEntries({
                                ...request,
                                limit: Math.min(NEWS_PAGINATION.batchSize, remaining),
                            }, offset, signal);
                            return {
                                items: page.entries.map((entry) => toNewsItem(entry, collection, resolveImage)),
                                nextOffset: data.limit > 0 && (page.nextOffset ?? 0) >= data.limit
                                    ? null
                                    : page.nextOffset,
                            };
                        }
                        : undefined }, request ? `${requestKey(request)}|${data.limit}` : "empty")) })] }));
}
/** The entries this block needs. Empty collectionId ⇒ no request at all. */
function requestFor(data) {
    if (!data.collectionId)
        return null;
    return {
        kind: "collection-entries",
        collectionId: data.collectionId,
        sort: data.sortField || undefined,
        // Inherit both parts of the collection's CMS ordering unless this block
        // selects a sort field. Sending the block's default direction alone would
        // override an ascending collection on the site but not in the CMS preview.
        dir: data.sortField ? data.sortDirection : undefined,
        limit: data.layout === "news-grid"
            ? Math.min(NEWS_PAGINATION.batchSize, data.limit || NEWS_PAGINATION.batchSize)
            : data.limit || undefined,
        filters: data.filters,
    };
}
export function collectionListFromBlock(block, _resolve) {
    return collectionListDataFrom(block);
}
export const collectionListBlockEntry = {
    Component: CollectionListBlock,
    fromBlock: collectionListFromBlock,
    dataRequests: (block) => {
        const request = requestFor(collectionListDataFrom(block));
        return request ? [request] : [];
    },
};
//# sourceMappingURL=collection-list.js.map